/**
 * Choosing the capsules a module is made of.
 *
 * A module is assembled by *selection*: a creator has capsules already and wants
 * six of them in a particular order. So this is a gallery, not a form — the cards
 * are small, they are tapped in the order the playlist should have them, and the
 * number that appears on each card is the number it will hold. That is the one
 * interaction the whole feature rests on, and it is why the selection is numbered
 * rather than ticked: a tick says "this belongs in the set", and what a playlist
 * needs is "this is third".
 *
 * Three decisions shape it:
 *
 *  - **It only ever offers the module's own subject and level.** The promise of a
 *    module — its card, its leaderboard, its whole reason to exist — is that its
 *    capsules belong together, and this is where that promise is kept. A card that
 *    could be chosen and then refused would be a rule the creator learns by being
 *    told off.
 *  - **Drafts are offered, and badged.** Writing a capsule *inside* a module is the
 *    other half of this flow and it starts with a draft, so a picker that hid
 *    drafts would send a creator looking for work they can see in their own list.
 *    The module refuses to publish until they are published, which is the rule said
 *    where it bites.
 *  - **It is one sheet, not a wizard.** The subject and level were chosen before
 *    this opened (a module without them cannot be assembled at all), so there is
 *    nothing left to ask: the search, the grid, and the two ways forward — add what
 *    is chosen, or write something new.
 */
import {useCallback, useMemo, useState} from 'react'
import {StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {AppBskyEmbedVideo} from '@atproto/api'
import {plural} from '@lingui/core/macro'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {type ModuleItem, modulePickerState} from '#/lib/modulePlaylist'
import {useActorCapsulesQuery} from '#/state/queries/capsule'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {useSession} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

export function ModuleCapsulePicker({
  control,
  level,
  subject,
  taken,
  alreadyPlaced,
  onAdd,
  onCreate,
  testIDPrefix = 'modulePicker',
}: {
  control: DialogControlProps
  level?: string | null
  subject?: string | null
  /** The capsule uris already in the playlist: never offered again. */
  taken: readonly string[]
  /** How many places are filled, for the "N more to publish" line. */
  alreadyPlaced: number
  onAdd: (items: ModuleItem[]) => void
  /** Write a new capsule *inside* this module, rather than choosing an old one. */
  onCreate: () => void
  testIDPrefix?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const {gtMobile} = useBreakpoints()
  const {currentAccount} = useSession()

  const {data: records, isPending} = useActorCapsulesQuery(currentAccount?.did)

  /** The order the creator tapped, which is the order the playlist gets. */
  const [order, setOrder] = useState<string[]>([])
  const [query, setQuery] = useState('')
  const [generation, setGeneration] = useState(0)

  /*
   * The sheet's state, decided in one place (`modulePickerState`): tapped
   * capsules stay on screen with their position, and the sheet only reads as
   * finished when the playlist already holds everything at this pair. The two
   * empty states differ: *nothing* filed at this pair means start writing,
   * *all-chosen* means the creator has finished choosing.
   */
  const {candidates, view, missingAfter} = useMemo(
    () =>
      modulePickerState(records, {
        level,
        subject,
        taken,
        order,
        alreadyPlaced,
      }),
    [alreadyPlaced, level, order, records, subject, taken],
  )

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return candidates
    return candidates.filter(candidate =>
      `${candidate.title} ${candidate.description}`
        .toLowerCase()
        .includes(needle),
    )
  }, [candidates, query])

  /*
   * The covers, batched.
   *
   * A capsule's film lives on its post, not on its record, so a gallery drawn from
   * records has to ask the posts for their stills or it renders a wall of empty
   * tiles. Drafts have no post and therefore no picture, which is exactly what the
   * badge on their card is for.
   */
  const postUris = useMemo(
    () =>
      shown
        .map(candidate => candidate.postUri)
        .filter((uri): uri is string => !!uri),
    [shown],
  )
  const {data: postViews} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)
  const thumbnailFor = useCallback(
    (postUri: string | undefined): string | undefined => {
      if (!postUri) return undefined
      const embed = viewsByUri.get(postUri)?.embed
      return embed && AppBskyEmbedVideo.isView(embed)
        ? embed.thumbnail
        : undefined
    },
    [viewsByUri],
  )

  const columns = gtMobile ? 3 : 2
  const rows = useMemo(() => {
    const out: (typeof shown)[] = []
    for (let index = 0; index < shown.length; index += columns) {
      out.push(shown.slice(index, index + columns))
    }
    return out
  }, [columns, shown])

  const toggle = useCallback((uri: string) => {
    setOrder(current =>
      current.includes(uri)
        ? current.filter(existing => existing !== uri)
        : [...current, uri],
    )
  }, [])

  const onConfirm = useCallback(() => {
    const chosen: ModuleItem[] = order.map(uri => {
      const record = (records ?? []).find(candidate => candidate.uri === uri)
      return {
        uri,
        ...(record?.cid ? {cid: record.cid} : null),
        ...(record?.postUri ? {postUri: record.postUri} : null),
      }
    })
    onAdd(chosen)
    setOrder([])
    control.close()
  }, [control, onAdd, order, records])

  const onWriteInstead = useCallback(() => {
    setOrder([])
    control.close()
    onCreate()
  }, [control, onCreate])

  const loading = isPending && candidates.length === 0
  const nothingAtAll = !isPending && view === 'nothing'
  const allChosen = !isPending && view === 'all-chosen'

  return (
    <Dialog.Outer control={control} onClose={() => setOrder([])}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        testID={`${testIDPrefix}Sheet`}
        label={l`Add capsules to this module`}
        style={[a.w_full, {maxWidth: 640}]}>
        <View style={[a.gap_md, a.pt_md]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_xl, a.font_bold]}>
              <Trans>Add capsules</Trans>
            </Text>
            <Text
              testID={`${testIDPrefix}Scope`}
              style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {l`A module is one subject at one level, so only ${
                subject ? l(subjectLabel(subject) ?? {message: subject}) : ''
              } · ${level ? l(gradeLabel(level) ?? {message: level}) : ''} capsules are shown — the ones that belong together.`}
            </Text>
          </View>

          {loading ? (
            <View style={[a.flex_row, a.align_center, a.gap_sm, a.py_2xl]}>
              <Loader size="sm" />
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                <Trans>Looking for your capsules…</Trans>
              </Text>
            </View>
          ) : allChosen ? (
            /*
              Nothing left to choose, and work that exists to have chosen from.
              Said as *finished* rather than as *empty*, and the way on is the
              builder itself — the playlist is right behind this sheet.
            */
            <View
              testID={`${testIDPrefix}AllChosen`}
              style={[
                a.gap_md,
                a.p_lg,
                a.rounded_md,
                a.align_center,
                t.atoms.bg_contrast_25,
              ]}>
              <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                <Trans>
                  Every capsule you have at this level is already in the module.
                </Trans>
              </Text>
              <Text
                style={[
                  a.text_sm,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  Write another one here if the playlist needs it, or close this
                  and reorder what is already in it.
                </Trans>
              </Text>
              <Button
                testID={`${testIDPrefix}CreateFromAllChosen`}
                label={l`Write a capsule for this module`}
                size="large"
                color="primary"
                onPress={onWriteInstead}>
                <ButtonText>
                  <Trans>Write a capsule</Trans>
                </ButtonText>
              </Button>
            </View>
          ) : nothingAtAll ? (
            /*
              The creator with nothing at this level: the case the empty module
              exists for. Not a dead end — the one thing that fixes it is the
              primary action, because a module is where a course is written.
            */
            <View
              testID={`${testIDPrefix}Empty`}
              style={[
                a.gap_md,
                a.p_lg,
                a.rounded_md,
                a.align_center,
                t.atoms.bg_contrast_25,
              ]}>
              <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                {l`You have no capsules at this level and subject yet.`}
              </Text>
              <Text
                style={[
                  a.text_sm,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  That is what an empty module is for: write the capsules it
                  needs, one at a time, and each one is an ordinary capsule —
                  published, or saved as a draft to come back to. The module
                  picks it up when you are done with it.
                </Trans>
              </Text>
              <Button
                testID={`${testIDPrefix}CreateFromEmpty`}
                label={l`Write the first capsule`}
                size="large"
                color="primary"
                onPress={onWriteInstead}>
                <ButtonText>
                  <Trans>Write the first capsule</Trans>
                </ButtonText>
              </Button>
            </View>
          ) : (
            <>
              <TextField.Root>
                <TextField.Input
                  key={`${testIDPrefix}-search-${generation}`}
                  testID={`${testIDPrefix}Search`}
                  label={l`Search your capsules`}
                  placeholder={l`Search your capsules`}
                  defaultValue={query}
                  onChangeText={setQuery}
                />
              </TextField.Root>

              {shown.length === 0 ? (
                <View
                  testID={`${testIDPrefix}NoMatch`}
                  style={[
                    a.gap_sm,
                    a.p_lg,
                    a.rounded_md,
                    a.align_center,
                    t.atoms.bg_contrast_25,
                  ]}>
                  <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                    <Trans>
                      Nothing left to choose at this level — everything that
                      matches is already in the module, or the search is
                      narrower than your library.
                    </Trans>
                  </Text>
                  <Button
                    label={l`Clear the search`}
                    color="secondary"
                    size="small"
                    onPress={() => {
                      setQuery('')
                      setGeneration(value => value + 1)
                    }}>
                    <ButtonText>
                      <Trans>Clear the search</Trans>
                    </ButtonText>
                  </Button>
                </View>
              ) : (
                /*
                  The grid hands its height back to the sheet.

                  It used to be a 360px scroller inside the sheet's own
                  scroller — two scrolls sharing a finger, which on a phone
                  means the one you wanted to move is the one that does not. One
                  scroll per surface: the sheet already scrolls, so the cards
                  just make it taller.
                */
                <View testID={`${testIDPrefix}Grid`} style={[a.gap_sm]}>
                  {rows.map(row => (
                    <View
                      key={row.map(candidate => candidate.uri).join(' ')}
                      style={[a.flex_row, a.gap_sm, a.align_stretch]}>
                      {row.map(candidate => {
                        const position = order.indexOf(candidate.uri)
                        return (
                          <View key={candidate.uri} style={[a.flex_1]}>
                            <CandidateCard
                              testID={`${testIDPrefix}Option-${candidate.uri}`}
                              title={
                                candidate.title.trim() || l`Untitled capsule`
                              }
                              coins={candidate.slideCount ?? 0}
                              draft={
                                candidate.status === 'draft' ||
                                !candidate.postUri
                              }
                              position={position >= 0 ? position + 1 : null}
                              thumbnail={thumbnailFor(candidate.postUri)}
                              onPress={() => toggle(candidate.uri)}
                            />
                          </View>
                        )
                      })}
                      {Array.from({length: columns - row.length}).map(
                        (_, index) => (
                          <View key={`filler-${index}`} style={[a.flex_1]} />
                        ),
                      )}
                    </View>
                  ))}
                </View>
              )}
            </>
          )}

          {!loading && !nothingAtAll && !allChosen ? (
            <View style={[a.gap_sm, a.pb_md]}>
              <Text
                testID={`${testIDPrefix}Count`}
                style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {order.length > 0 ? (
                  <Trans>
                    {order.length} selected · {alreadyPlaced + order.length} in
                    the module
                  </Trans>
                ) : (
                  <Trans>{alreadyPlaced} in the module</Trans>
                )}
                {missingAfter > 0
                  ? ` · ${plural(missingAfter, {
                      one: '# more capsule needed to publish',
                      other: '# more capsules needed to publish',
                    })}`
                  : ''}
              </Text>
              <Button
                testID={`${testIDPrefix}Submit`}
                label={l`Add to the module`}
                size="large"
                color="primary"
                disabled={order.length === 0}
                onPress={onConfirm}>
                <ButtonText>
                  {order.length > 0 ? (
                    <Plural
                      value={order.length}
                      one="Add # capsule"
                      other="Add # capsules"
                    />
                  ) : (
                    <Trans>Add to the module</Trans>
                  )}
                </ButtonText>
              </Button>
              <Button
                testID={`${testIDPrefix}Create`}
                label={l`Write a new capsule`}
                color="secondary"
                size="small"
                onPress={onWriteInstead}>
                <ButtonText>
                  <Trans>Write a new capsule instead</Trans>
                </ButtonText>
              </Button>
            </View>
          ) : null}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * One capsule, as a choice.
 *
 * The selection number is the card's whole interaction: tapping a card puts it at
 * the end of the playlist and stamps the number on it, and tapping it again takes
 * it out and closes the gap. A cover when the post has one, and the title either
 * way — a draft has no picture, and a card with no picture and no words would be
 * a card nobody can choose.
 */
function CandidateCard({
  testID,
  title,
  coins,
  draft,
  position,
  thumbnail,
  onPress,
}: {
  testID: string
  title: string
  coins: number
  draft: boolean
  position: number | null
  thumbnail?: string
  onPress: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <Button
      testID={testID}
      label={l`${title}${position ? `, number ${position}` : ''}`}
      onPress={onPress}>
      {({hovered, pressed}) => (
        <View
          style={[
            styles.card,
            position ? {borderColor: t.palette.primary_500} : null,
            t.atoms.bg_contrast_25,
            hovered || pressed ? t.atoms.bg_contrast_50 : null,
          ]}>
          <View style={[styles.tile, t.atoms.bg_contrast_50]}>
            {thumbnail ? (
              <Image
                source={{uri: thumbnail}}
                style={StyleSheet.absoluteFill}
                contentFit="cover"
                accessibilityIgnoresInvertColors
              />
            ) : null}
            {position ? (
              <View
                testID={`${testID}-selected`}
                style={[
                  styles.number,
                  {backgroundColor: t.palette.primary_500},
                ]}>
                <Text style={[a.text_xs, a.font_bold, styles.onAccent]}>
                  {position}
                </Text>
              </View>
            ) : null}
            {draft ? (
              <View style={[styles.draft, styles.darkChip]}>
                <Text style={[a.text_xs, a.font_semi_bold, styles.onAccent]}>
                  <Trans>Draft</Trans>
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            numberOfLines={2}
            style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>
            {title}
          </Text>
          {coins > 0 ? (
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              <Plural value={coins} one="# coin" other="# coins" />
            </Text>
          ) : null}
        </View>
      )}
    </Button>
  )
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    gap: 6,
    padding: 6,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  tile: {
    width: '100%',
    aspectRatio: 4 / 5,
    borderRadius: 8,
    overflow: 'hidden',
  },
  number: {
    position: 'absolute',
    top: 6,
    left: 6,
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  draft: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
  },
  darkChip: {backgroundColor: 'rgba(0, 0, 0, 0.6)'},
  onAccent: {color: 'white'},
})
